/**
 * CreditsPage — /credits (Stage 08.5 WAVE-4 REDESIGN + radio-model alignment 2026-05-23).
 *
 * Figma source: `319:4731` — 3 credit packages 1000/300/100, multi-currency.
 *
 * RADIO-MODEL alignment (2026-05-23, parity with /plans):
 *   - Per-card «Купить» button удалён — карточка тапается целиком как radio
 *   - Добавлен selectedPackId state + handleSelectPack
 *   - Bottom CTA «Купить за …» отражает выбранный пакет (ctaPack), а не статичный packages[0]
 *   - Pattern зеркалит Plans.jsx (selectedPlanId / ctaPlan / handleCtaConfirm)
 *
 * P0 HOTFIX (2026-05-22) per plans-design-review-2026-05-22.md:
 *   - Header safe-area pattern (HomeStatusBar.jsx clone) — убран double-padding
 *   - TrustLogosBlock перенесён вниз скролла (всегда видим, не только под card)
 *   - CTA: убран leftIcon=coin; rightIcon условный (star только для Stars)
 *   - CTA Stars text: убран inline ⭐
 *
 * WAVE-4 changes (REOPENED 2026-05-22):
 *   - Native-style statusbar header + centered Raleway Bold 18 «Покупка кредитов»
 *   - PaymentMethodPicker square cards 86.5×98 (same as Plans)
 *   - «Метод оплаты» label above picker
 *   - CTA «Купить за …» fixed bottom 402×88 container
 *   - PaymentMethodPicker state INDEPENDENT from /plans (own useState)
 *
 * API:
 *  GET /api/credits/packages → { packages: [...] }
 *  POST /api/credits/checkout/stripe → { checkout_url }
 *  POST /api/credits/invoice/telegram → Stars invoice flow
 *
 * Правила:
 *  - PaymentMethodPicker state независим от /plans (свой useState)
 *  - 401 → navigate('/')
 *  - CTA всегда активна; покупка повторно — допустима (handoff §15)
 */

import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PaymentMethodPicker from '../components/payments/PaymentMethodPicker';
import CryptoCurrencyPicker, {
  DEFAULT_CRYPTO_CURRENCY,
} from '../components/payments/CryptoCurrencyPicker';
import CryptoPaymentDetails from '../components/payments/CryptoPaymentDetails';
import TrustLogosBlock from '../components/payments/TrustLogosBlock';
import CreditPackageCard from '../components/credits/CreditPackageCard';
import { UIButton } from '../components/primitives';
import { getDefaultMethod } from '../utils/payment-availability';
import { useCreditsPackagesQuery } from '../core/queries/account';
import {
  useCreditsCheckoutP4yMutation,
  useCreditsCheckoutStripeMutation,
  useCreditsInvoiceTelegramMutation,
} from '../core/queries/mutations';
import { useCreditsPaymentStatusQuery } from '../core/queries/paymentStatus';
import { queryKeys } from '../core/queries/queryKeys';
import { useAuthStore } from '../store/authStore';
import { getLanguageCode, openExternalUrl, purchase } from '../platform';
import useConsentGuard from '../hooks/useConsentGuard';

// Detect locale + telegramLang from Telegram WebApp
function getPaymentLocale(appLanguage) {
  const lang = getLanguageCode() || null;
  return {
    locale: String(appLanguage || lang || '').toLowerCase().startsWith('ru') ? 'ru' : '',
    telegramLang: lang,
  };
}

// Sort packages in required order: 1000 → 300 → 100 (handoff §15)
const SORT_ORDER = [1000, 300, 100];
function sortPackages(packages) {
  if (!Array.isArray(packages)) return [];
  return [...packages].sort((a, b) => {
    const ai = SORT_ORDER.indexOf(a.credits);
    const bi = SORT_ORDER.indexOf(b.credits);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

/** Narrow no-break space thousands separator (U+202F per Figma spec) */
function formatThousands(n) {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/**
 * Format price for CTA text depending on method.
 * Stars: число без `⭐` — звезда теперь как rightIcon на UIButton (Figma 40000032:6643).
 */
const USD_TO_STARS = 50;
const USD_TO_RUB = 100;

function formatCtaPrice(pack, method) {
  if (!pack) return '';
  if (method === 'stars') {
    const stars = Math.ceil(pack.price * USD_TO_STARS);
    return formatThousands(stars);
  }
  if (method === 'sbp') {
    return `${formatThousands(Math.round(pack.price * USD_TO_RUB))} ₽`;
  }
  return `$${pack.price}`;
}

export default function CreditsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { requireConsent } = useConsentGuard();
  const queryClient = useQueryClient();
  const refreshUser = useAuthStore((s) => s.refreshUser);

  // Payment method — INDEPENDENT state (not shared with /plans)
  const [method, setMethod] = useState(getDefaultMethod());
  const [cryptoCurrency, setCryptoCurrency] = useState(DEFAULT_CRYPTO_CURRENCY);
  const [showCryptoPicker, setShowCryptoPicker] = useState(false);
  const [cryptoPayment, setCryptoPayment] = useState(null);
  const { locale, telegramLang } = getPaymentLocale(
    i18n?.resolvedLanguage || i18n?.language,
  );

  const packagesQuery = useCreditsPackagesQuery();
  const starsCheckoutMutation = useCreditsInvoiceTelegramMutation();
  const p4yCheckoutMutation = useCreditsCheckoutP4yMutation();
  const stripeCheckoutMutation = useCreditsCheckoutStripeMutation();
  const [error, setError] = useState(null);
  const paymentStatusQuery = useCreditsPaymentStatusQuery(cryptoPayment?.payment_id, {
    enabled: Boolean(cryptoPayment?.payment_id),
    expiresAt: cryptoPayment?.expires_at,
  });
  const cryptoPaymentStatus = paymentStatusQuery.data?.status;
  const cryptoPaymentFailed =
    paymentStatusQuery.isPollingTimedOut ||
    cryptoPaymentStatus === 'failed' ||
    cryptoPaymentStatus === 'expired' ||
    paymentStatusQuery.isError;

  // Selected pack for CTA (null = первый в списке = featured 1000)
  const [selectedPackId, setSelectedPackId] = useState(null);

  // CTA loading state (один CTA на странице, не per-card)
  const [buyingId, setBuyingId] = useState(null);

  useEffect(() => {
    if (packagesQuery.error?.response?.status === 401) navigate('/');
  }, [navigate, packagesQuery.error]);

  useEffect(() => {
    if (method !== 'crypto') {
      setShowCryptoPicker(false);
    }
    if (method !== 'crypto' && method !== 'sbp') {
      setCryptoPayment(null);
    }
  }, [method]);

  useEffect(() => {
    if (paymentStatusQuery.data?.status !== 'paid') return;

    queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
    queryClient.invalidateQueries({ queryKey: queryKeys.creditsPackages() });
    queryClient.invalidateQueries({ queryKey: ['history'] });
    queryClient.invalidateQueries({ queryKey: ['user-history'] });
    refreshUser?.();
  }, [paymentStatusQuery.data?.status, queryClient, refreshUser]);

  const performBuy = useCallback(
    async (pack) => {
      if (buyingId) return;

      if (method === 'crypto' && !showCryptoPicker) {
        setShowCryptoPicker(true);
        setError(null);
        return;
      }

      // MOCK mode: stub checkout, navigate to /
      const isMock =
        import.meta.env.VITE_MOCK_GENERATION === 'true' ||
        import.meta.env.MODE === 'test';
      if (isMock) {
        navigate('/');
        return;
      }

      setBuyingId(pack.id);
      setError(null);

      try {
        if (method === 'stars') {
          const invoiceRes = await starsCheckoutMutation.mutateAsync({
            credits: pack.credits,
            packageId: pack.id,
          });
          if (invoiceRes?.invoice_url) {
            purchase(
              {
                productId: `credits:${pack.id}`,
                checkoutUrl: invoiceRes.invoice_url,
              },
              {
                onStatus: (status) => {
                  if (status === 'paid') {
                    queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
                    queryClient.invalidateQueries({ queryKey: queryKeys.creditsPackages() });
                    queryClient.invalidateQueries({ queryKey: ['history'] });
                    queryClient.invalidateQueries({ queryKey: ['user-history'] });
                    navigate('/');
                  }
                },
              },
            );
          }
        } else if (method === 'crypto' || method === 'sbp') {
          const res = await p4yCheckoutMutation.mutateAsync({
            credits: pack.credits,
            packageId: pack.id,
            paymentMethod: method,
            cryptoCurrency: method === 'crypto' ? cryptoCurrency : 'sbp',
          });
          setCryptoPayment(res);
        } else {
          const res = await stripeCheckoutMutation.mutateAsync({
            credits: pack.credits,
            packageId: pack.id,
          });
          const { checkout_url } = res;
          if (checkout_url) {
            if (!openExternalUrl(checkout_url)) {
              window.location.assign(checkout_url);
            }
          }
        }
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || t('plans.errorPayment'));
      } finally {
        setBuyingId(null);
      }
    },
    [
      method,
      cryptoCurrency,
      showCryptoPicker,
      buyingId,
      navigate,
      queryClient,
      t,
      starsCheckoutMutation,
      p4yCheckoutMutation,
      stripeCheckoutMutation,
    ],
  );

  const handleBuy = useCallback(
    (pack) => requireConsent(() => performBuy(pack), 'purchase'),
    [performBuy, requireConsent],
  );

  // Card tap — pure selection, no side effects (Figma 319:4731 radio model,
  // зеркалит Plans.jsx handleSelectPlan).
  const handleSelectPack = useCallback((pack) => {
    setSelectedPackId(pack.id);
  }, []);

  // Loading state
  const rawPackages =
    packagesQuery.data?.packages ||
    packagesQuery.data?.items ||
    packagesQuery.data ||
    [];
  const packages = sortPackages(Array.isArray(rawPackages) ? rawPackages : []);
  const loading = packagesQuery.isLoading;
  const loadError = packagesQuery.isError ? packagesQuery.error : null;

  if (loading) {
    return (
      <div
        className="min-h-screen bg-bg-app flex items-center justify-center"
        aria-label={t('common.loading')}
      >
        <span className="font-sans text-plan text-text-secondary">{t('common.loading')}</span>
      </div>
    );
  }

  // CTA pack = selected or first (best choice = 1000)
  const ctaPack = packages.find((p) => p.id === selectedPackId) ?? packages[0] ?? null;
  const isCryptoChoiceStep = method === 'crypto' && showCryptoPicker;
  const isCryptoPaymentStep = Boolean(cryptoPayment);

  return (
    <div className="min-h-screen bg-bg-app flex flex-col text-text-primary relative">
      {/* ── Statusbar header (Figma 319:4732 — 96px gradient + shine glow + title) ──
          Safe-area pattern mirrors HomeStatusBar.jsx — НЕ hardcoded height. */}
      <div
        className="relative shrink-0"
        style={{
          paddingTop: 'calc(var(--safe-top, 0px) + 12px)',
          paddingBottom: '12px',
        }}
      >
        {/* Scrim gradient #000 → transparent. */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.00) 100%)',
          }}
        />
        {/* Shine ellipse 104×104 #FFAC0B blur 170 (Figma I319:4732;318:3534). */}
        <div
          aria-hidden
          data-testid="credits-statusbar-shine"
          className="absolute top-0 left-[31px] w-[104px] h-[104px] rounded-full pointer-events-none"
          style={{ background: '#FFAC0B', filter: 'blur(170px)' }}
        />
        <h1
          className="relative font-sans font-bold text-[18px] leading-[21.13px] tracking-[0.36px] text-text-primary text-center uppercase"
          style={{
            transform: 'translateY(var(--page-title-shift, 47px))',
            maxWidth: 'calc(100% - 200px)',
            marginInline: 'auto',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {t('credits.screenTitle')}
        </h1>
      </div>

      {/* ── Scrollable content ── */}
      <div
        className="flex-1 overflow-y-auto"
        style={{
          paddingTop: 'var(--page-title-clearance, 48px)',
          paddingBottom: 'calc(88px + env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        {isCryptoPaymentStep && (
          <>
            <CryptoPaymentDetails
              payment={cryptoPayment}
              onBack={() => setCryptoPayment(null)}
            />
            {cryptoPaymentStatus !== 'paid' && !cryptoPaymentFailed && (
              <p className="px-4 pb-4 text-center font-sans text-[12px] text-text-secondary">
                {t('success.waitingConfirmation', 'Waiting for payment confirmation...')}
              </p>
            )}
            {cryptoPaymentFailed && (
              <p className="px-4 pb-4 text-center font-sans text-[12px] text-text-secondary" role="alert">
                {t('success.confirmationTimeout', 'Payment is not confirmed yet. If you paid, reopen the app later and the balance will refresh automatically.')}
              </p>
            )}
          </>
        )}

        {!isCryptoPaymentStep && !isCryptoChoiceStep && (
          <>
            {/* «Метод оплаты» label */}
            <div className="px-4 mb-2">
              <span className="font-sans font-medium text-[12px] leading-[12px] text-text-secondary">
                {t('plans.paymentMethod')}
              </span>
            </div>

            {/* ── PaymentMethodPicker (square cards) — independent state ── */}
            <div className="px-4 mb-4">
              <PaymentMethodPicker
                value={method}
                onChange={setMethod}
                locale={locale}
                telegramLang={telegramLang}
              />
            </div>
          </>
        )}

        {!isCryptoPaymentStep && isCryptoChoiceStep && ctaPack && (
          <div className="px-4 mb-4 space-y-3">
            <section className="rounded-home-16 bg-glass-8 border border-border-default p-4">
              <p className="font-sans font-medium text-[11px] leading-[13px] text-text-secondary">
                {t('plans.cryptoPicker.selected')}
              </p>
              <div className="mt-2 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-sans font-bold text-[18px] leading-[21px] text-text-primary">
                    {formatThousands(ctaPack.credits)} Credits
                  </p>
                  <p className="mt-1 font-sans font-semibold text-[12px] leading-[14px] text-text-secondary">
                    {t('credits.screenTitle')}
                  </p>
                </div>
                <p className="shrink-0 font-sans font-extrabold text-[24px] leading-[28px] text-text-primary">
                  {formatCtaPrice(ctaPack, method)}
                </p>
              </div>
            </section>
            <CryptoCurrencyPicker
              value={cryptoCurrency}
              onChange={setCryptoCurrency}
            />
          </div>
        )}

        {/* ── Error state ── */}
        {(error || loadError) && !loading && !isCryptoPaymentStep && (
          <div
            className="mx-4 mb-4 px-4 py-3 rounded-home-12 bg-glass-8 border border-border-default"
            role="alert"
          >
            <p className="font-sans text-plan text-text-secondary text-center">
              {typeof error === 'string' ? error : t('common.error')}
            </p>
          </div>
        )}

        {/* ── Credit package cards — 1000 → 300 → 100 ── */}
        {!isCryptoPaymentStep && !isCryptoChoiceStep && (
          <div className="px-4 flex flex-col gap-4">
            {packages.map((pack) => (
              <CreditPackageCard
                key={pack.id}
                pack={pack}
                method={method}
                isSelected={ctaPack?.id === pack.id}
                onSelect={handleSelectPack}
              />
            ))}

            {/* Empty state */}
            {packages.length === 0 && !loading && !error && !loadError && (
              <div className="text-center py-10">
                <p className="font-sans text-plan text-text-secondary">
                  {t('credits.packagesUnavailable')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── TrustLogosBlock — Figma 319:4759 always visible at bottom of scroll
            (между списком пакетов и CTA-zone). Перенесён сверху сюда per P0-2. */}
        {!isCryptoPaymentStep && !isCryptoChoiceStep && packages.length > 0 && (
          <div className="px-4 mt-4">
            <TrustLogosBlock />
          </div>
        )}
      </div>

      {/* ── Fixed CTA container (Figma 319:4829 — 402×88, top border #fff@16, bg #fff@4) ──
          P0-3: leftIcon удалён, rightIcon=star только для Stars. */}
      {ctaPack && !isCryptoPaymentStep && (
        <div
          className="fixed bottom-0 left-0 right-0 z-10 flex items-center justify-center"
          style={{
            height: 88,
            borderTop: '1px solid rgba(255,255,255,0.16)',
            backgroundColor: 'rgba(255,255,255,0.04)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <UIButton
            type="primary"
            size="lg"
            rightIcon={method === 'stars' ? 'star' : undefined}
            onClick={() => handleBuy(ctaPack)}
            disabled={!!buyingId}
            className="bg-primary-btn-gradient"
            style={{
              width: 370,
              opacity: buyingId ? 0.7 : 1,
            }}
            data-testid="credits-cta-btn"
          >
            {buyingId
              ? t('common.processing')
              : method === 'crypto' && showCryptoPicker
                ? `${t('plans.cryptoPicker.pay')} ${formatCtaPrice(ctaPack, method)}`
                : `${t('credits.buyFor')} ${formatCtaPrice(ctaPack, method)}`}
          </UIButton>
        </div>
      )}
    </div>
  );
}
